/**
 * Web entry point.
 *
 * Called from index.html via <script type="module">.  Registers the
 * same App component that native uses with react-native-web's
 * AppRegistry, then mounts it onto #root.  The `initialProps` object
 * is empty — all initial state comes from URL-based deep linking that
 * NavigationContainer handles via the linking config.
 *
 * Also injects the @font-face declaration for Ionicons (the only icon
 * family the app currently uses).  react-native-vector-icons ships
 * the .ttf inside its npm package; on native we copy it via
 * `fonts.gradle` / pod resources, but on web we have to load it
 * ourselves at the document level — otherwise every <Ionicons />
 * renders as a missing-glyph rectangle.  See `installIoniconsFont()`
 * below for the mechanism (Vite's `?url` import + a runtime
 * <style> tag).
 */
import { AppRegistry } from 'react-native';
import App from '../App';

// Vite's `?url` query asks the build to emit the asset and hand back
// a URL that points at the (hashed) file in dist/.  At dev-server
// time it's an unhashed path under /node_modules/.../Ionicons.ttf.
// Either way `ioniconsUrl` is a string we can drop into a CSS @font-face.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — Vite-specific virtual import; no .d.ts ships with the asset
import ioniconsUrl from 'react-native-vector-icons/Fonts/Ionicons.ttf?url';

/**
 * Inject a single @font-face into <head> declaring the Ionicons font
 * family.  Idempotent (no-op on a second call) so React strict-mode
 * double-mounts don't double-insert.
 */
function installIoniconsFont() {
  if (typeof document === 'undefined') return; // SSR / non-browser
  if (document.getElementById('rinsepay-ionicons-font')) return;
  const style = document.createElement('style');
  style.id = 'rinsepay-ionicons-font';
  style.textContent = `@font-face {
  font-family: 'Ionicons';
  src: url('${ioniconsUrl}') format('truetype');
  font-weight: normal;
  font-style: normal;
  font-display: block;
}`;
  document.head.appendChild(style);
}

installIoniconsFont();

AppRegistry.registerComponent('main', () => App);

const rootTag = document.getElementById('root');
if (rootTag) {
  AppRegistry.runApplication('main', { rootTag });
} else {
  // Should never happen unless index.html was edited incorrectly
  // eslint-disable-next-line no-console
  console.error('RinsePay web: #root not found in DOM');
}
